import { cn } from "@/lib/utils";
import { copyToClipboard } from "@/lib/utils/clipboard";
import {
  CaretDownIcon,
  CaretUpIcon,
  CheckIcon,
  CopyIcon,
} from "@phosphor-icons/react";
import { useCallback, useRef, useState } from "react";
import { FeedMetricsRenderer } from "./FeedMetricsRenderer";
import { useMapMetricsToStats } from "./useGetFeedMetrics";

/**
 * FeedMetrics provides a collapsible container for FeedMetricsRenderer
 * with copy to clipboard functionality, similar to TotalStats component
 */
const FeedMetrics = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch and map feed metrics data
  const { stats: displayStats, fetching: isLoading } = useMapMetricsToStats();

  const handleCopyStats = useCallback(() => {
    const copyString = displayStats
      .map((stat) => `${stat.label}: ${stat.rawValue}`)
      .join(" | ");
    copyToClipboard(copyString);
    setIsCopied(true);

    // Reset the copied state after 3 seconds
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setIsCopied(false);
    }, 3000);
  }, [displayStats]);

  const renderCopyButton = useCallback(() => {
    return (
      <div
        className="flex flex-row gap-2 items-center cursor-pointer"
        onClick={handleCopyStats}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleCopyStats();
          }
        }}
      >
        <div className="flex flex-row gap-1 items-center">
          {isCopied ? (
            <>
              <CheckIcon className="w-[14px] h-[14px] text-green-600" />
              <p className="text-xxs text-gray-900 italic">
                Copied to clipboard!
              </p>
            </>
          ) : (
            <>
              <CopyIcon className="w-[14px] h-[14px] text-gray-900" />
              <p className="text-xxs text-gray-700 hover:text-gray-900 tracking-tight">
                Copy stats
              </p>
            </>
          )}
        </div>
      </div>
    );
  }, [handleCopyStats, isCopied]);

  const renderCollapseButton = useCallback(() => {
    return (
      <div
        className="flex flex-row gap-1 items-center cursor-pointer"
        aria-expanded={!isCollapsed}
        aria-label={
          !isCollapsed ? "Expand feed metrics" : "Collapse feed metrics"
        }
        onClick={() => setIsCollapsed(!isCollapsed)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsCollapsed(!isCollapsed);
          }
        }}
      >
        <p className="text-xs font-semibold text-gray-600">Total Stats</p>
        {isCollapsed ? (
          <CaretUpIcon className="w-4 h-4 text-gray-900" weight="fill" />
        ) : (
          <CaretDownIcon className="w-4 h-4 text-gray-900" weight="fill" />
        )}
      </div>
    );
  }, [isCollapsed]);

  return (
    <div className="w-full max-w-7xl mx-auto py-2 px-2">
      <div className="w-full bg-white">
        <div className="flex flex-col gap-3 overflow-x-hidden max-w-[932px] mx-auto py-2">
          {/* Header */}
          <div className="sticky top-0 bg-white flex flex-row gap-2 items-center justify-between">
            {renderCollapseButton()}
            {!isLoading && renderCopyButton()}
          </div>

          {/* Metrics */}
          <div
            className={cn(
              "overflow-hidden transition-all duration-300 ease-in-out",
              isCollapsed ? "max-h-0 opacity-0" : "max-h-96 opacity-100",
            )}
          >
            <div className="transition-all duration-300 ease-in-out">
              <FeedMetricsRenderer stats={displayStats} isLoading={isLoading} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedMetrics;
